import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  api,
  ApiError,
  type PlatformUsageResponse,
  type UsageSummaryResponse,
  type LicenseUsageRow,
} from "../api.js";
import { useAuth, useToken } from "../auth.js";
import { RequestPricing, formatRecordedUsd } from "../components/RequestPricing.js";

// Consumption dashboard §"Endpoints de consommation". Sorted by cost desc so
// operators see the biggest spenders first. Rows over 80% of budget get a
// visual alert badge; rows over 100% (into the tolerance band) get a stronger
// tone. The month picker defaults to the current UTC month.

export function Usage() {
  const token = useToken();
  const [data, setData] = useState<UsageSummaryResponse | null>(null);
  const [month, setMonth] = useState<string>(defaultMonth());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"cost" | "name" | "budget">("cost");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.usageSummary(token, month);
      setData(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token, month]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const rows = useMemo(() => {
    if (!data) return [] as Array<LicenseUsageRow & { customerName: string }>;
    const arr = data.customers.flatMap(c => c.licenses.map(l => ({ ...l, customerName: c.customerName })));
    if (sortBy === "cost") arr.sort((a, b) => b.projectedUsd - a.projectedUsd);
    if (sortBy === "name") arr.sort((a, b) => a.customerName.localeCompare(b.customerName));
    if (sortBy === "budget") arr.sort((a, b) => b.hardCapUsedPct - a.hardCapUsedPct);
    return arr;
  }, [data, sortBy]);

  const { t } = useLingui();

  return (
    <div>
      <header className="page-header">
        <h1><Trans>Usage & cost</Trans></h1>
        <div className="row-actions">
          <label>
            <Trans>Month</Trans>{" "}
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </label>
          <button className="ghost" onClick={() => void refresh()}>
            <Trans>Refresh</Trans>
          </button>
        </div>
      </header>
      {error && <div className="banner err">{error}</div>}
      {loading || !data ? (
        <p className="muted"><Trans>Loading…</Trans></p>
      ) : (
        <>
          <section className="kpi-row">
            <KpiCard label={t`Total cost`} value={formatUsd(data.totals.costUsd)} />
            <KpiCard
              label={t`Held reservations`}
              value={formatUsd(data.totals.heldReservationsUsd)}
            />
            <KpiCard
              label={t`Input tokens`}
              value={formatBigNum(data.totals.inputTokens)}
            />
            <KpiCard
              label={t`Output tokens`}
              value={formatBigNum(data.totals.outputTokens)}
            />
          </section>
          <p className="muted"><Trans>Remaining allowance shows budget and hard cap slack for each licence.</Trans></p>
          <table className="table">
            <thead>
              <tr>
                <th>
                  <SortLabel active={sortBy === "name"} onClick={() => setSortBy("name")}>
                    <Trans>Customer</Trans>
                  </SortLabel>
                </th>
                <th><Trans>Licence</Trans></th>
                <th><Trans>Plan</Trans></th>
                <th style={{ textAlign: "right" }}>
                  <SortLabel active={sortBy === "cost"} onClick={() => setSortBy("cost")}>
                    <Trans>Spent (mo)</Trans>
                  </SortLabel>
                </th>
                <th style={{ textAlign: "right" }}><Trans>Held</Trans></th>
                <th style={{ textAlign: "right" }}><Trans>Budget</Trans></th>
                <th><Trans>Tolerance</Trans></th>
                <th><Trans>Remaining allowance</Trans></th>
                <th><Trans>Status</Trans></th>
                <th>
                  <SortLabel
                    active={sortBy === "budget"}
                    onClick={() => setSortBy("budget")}
                  >
                    <Trans>Usage</Trans>
                  </SortLabel>
                </th>
                <th style={{ textAlign: "right" }}><Trans>Turns</Trans></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.licenseId}>
                  <td>
                    <Link to={`/customers/${r.customerId}`}>{r.customerName}</Link>
                  </td>
                  <td>{r.label ?? r.licenseKey}</td>
                  <td>
                    <code title={r.plan.name}>{r.plan.code}</code>
                  </td>
                  <td style={{ textAlign: "right" }}>{formatUsd(r.costUsd)}</td>
                  <td style={{ textAlign: "right" }}>
                    {r.heldReservationsUsd > 0 ? formatUsd(r.heldReservationsUsd) : "—"}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatUsd(r.plan.monthlyBudgetUsd)}
                  </td>
                  <td>{r.plan.tolerancePct}%</td>
                  <td>{formatUsd(r.remainingBudgetUsd)} / {formatUsd(r.remainingHardCapUsd)}</td>
                  <td>{r.exhausted ? <Trans>Exhausted</Trans> : <Trans>Available</Trans>}</td>
                  <td>
                    <UsageBar
                      spent={r.projectedUsd}
                      budget={r.plan.monthlyBudgetUsd}
                      hardCap={r.plan.hardCapUsd}
                    />
                  </td>
                  <td style={{ textAlign: "right" }}>{r.turnCount}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={11} className="muted" style={{ textAlign: "center" }}>
                    <Trans>No usage recorded this month.</Trans>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <PlatformSpend month={month} />
        </>
      )}
    </div>
  );
}

// Jalfa's own LLM spend for the month: model calls the product makes for itself
// and bills to nobody — today, the question rewriter that turns a French
// question into the Sage schema's English vocabulary before the discovery tools
// search it. It sits under the customer table rather than in the KPI row on
// purpose: it is not part of what anyone owes, and adding it to those totals is
// exactly the confusion the separate ledger exists to prevent.
//
// Superuser-only, matching the API. A reseller gets nothing rendered, not an
// error — the endpoint would 403 and there is nothing here for them.
function PlatformSpend({ month }: { month: string }) {
  const token = useToken();
  const { user, emergency } = useAuth();
  const isSuperuser = emergency || user?.role === "superuser";
  const [data, setData] = useState<PlatformUsageResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const { i18n } = useLingui();

  useEffect(() => { setPage(1); }, [month]);

  useEffect(() => {
    if (!isSuperuser) return;
    let cancelled = false;
    setData(null);
    setError(null);
    void api
      .platformUsage(token, month, page)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [token, month, isSuperuser, page]);

  if (!isSuperuser) return null;
  if (error) return <div className="banner err">{error}</div>;
  if (!data || data.byPurpose.length === 0) return null;

  return (
    <section style={{ marginTop: 32 }}>
      <h2><Trans>Platform spend (Jalfa)</Trans></h2>
      <p className="muted">
        <Trans>
          Model calls the product makes for itself. Not billed to any customer,
          not counted in any quota — this is our own cost.
        </Trans>
      </p>
      <table className="table">
        <thead>
          <tr>
            <th><Trans>Purpose</Trans></th>
            <th><Trans>Model</Trans></th>
            <th style={{ textAlign: "right" }}><Trans>Calls</Trans></th>
            <th style={{ textAlign: "right" }}><Trans>Input</Trans></th>
            <th style={{ textAlign: "right" }}><Trans>Output</Trans></th>
            <th style={{ textAlign: "right" }}><Trans>Cost</Trans></th>
          </tr>
        </thead>
        <tbody>
          {data.byPurpose.map((b) => (
            <tr key={`${b.purpose}:${b.modelId}`}>
              <td><code>{b.purpose}</code></td>
              <td>
                {b.provider} · {b.modelName ?? b.modelId}
              </td>
              <td style={{ textAlign: "right" }}>{b.calls}</td>
              <td style={{ textAlign: "right" }}>{formatBigNum(b.inputTokens)}</td>
              <td style={{ textAlign: "right" }}>{formatBigNum(b.outputTokens)}</td>
              <td style={{ textAlign: "right" }}>{formatRecordedUsd(b.costUsd, i18n.locale)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={5} style={{ textAlign: "right", fontWeight: 700 }}>
              <Trans>Total</Trans>
            </td>
            <td style={{ textAlign: "right", fontWeight: 700 }}>
              {formatRecordedUsd(data.totals.costUsd, i18n.locale)}
            </td>
          </tr>
        </tbody>
      </table>
      <h3><Trans>Platform requests</Trans></h3>
      {data.requests.map((request) => (
        <details key={request.id} className="conversation-turn">
          <summary>
            <Trans>Question rewrite</Trans> · {request.provider} · {request.modelId} · {formatRecordedUsd(request.costUsd, i18n.locale)}
          </summary>
          {request.conversationId && <p><Link to={`/conversations/${request.conversationId}`}><Trans>View conversation</Trans></Link></p>}
          <RequestPricing request={request} />
        </details>
      ))}
      <div className="row-actions">
        <button className="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}><Trans>Previous</Trans></button>
        <span><Trans>Page {page} of {Math.max(1, Math.ceil(data.requestPagination.total / data.requestPagination.pageSize))}</Trans></span>
        <button className="ghost" disabled={page * data.requestPagination.pageSize >= data.requestPagination.total} onClick={() => setPage(page + 1)}><Trans>Next</Trans></button>
      </div>
    </section>
  );
}

function SortLabel({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className="link-btn"
      style={{ fontWeight: active ? 700 : 400 }}
      onClick={onClick}
    >
      {children} {active ? "▾" : ""}
    </button>
  );
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}

function UsageBar({
  spent,
  budget,
  hardCap,
}: {
  spent: number;
  budget: number;
  hardCap: number;
}) {
  const { t } = useLingui();
  const pctOfBudget = budget > 0 ? (spent / budget) * 100 : 0;
  const pctOfCap = hardCap > 0 ? (spent / hardCap) * 100 : 0;
  const tone =
    pctOfCap >= 100
      ? "critical"
      : pctOfBudget >= 100
        ? "warn"
        : pctOfBudget >= 80
          ? "notice"
          : "ok";
  const pctDisplay = Math.min(pctOfCap, 120);
  return (
    <div
      className={`usage-bar tone-${tone}`}
      title={t`${pctOfBudget.toFixed(0)}% of budget · ${pctOfCap.toFixed(0)}% of hard cap`}
    >
      <div className="usage-bar-fill" style={{ width: `${Math.min(100, pctDisplay)}%` }} />
      <span className="usage-bar-label">
        <Trans>{pctOfBudget.toFixed(0)}% of budget</Trans>
      </span>
    </div>
  );
}

function formatUsd(v: number): string {
  return `$${v.toFixed(2)}`;
}

function formatBigNum(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
}

function defaultMonth(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${(d.getUTCMonth() + 1).toString().padStart(2, "0")}`;
}
